/**
 * ManufacturingOpsPanel — Factory-scoped WIP + DPR + Delays widget.
 * Drops into FactoryDashboardPage and (in read-only view) the Executive Dashboard.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate, inr } from "@/lib/utils-format";
import { PlayCircle, PackageCheck, ClipboardList, AlertOctagon, Plus, Trash2, Factory, CheckCircle2 } from "lucide-react";

const DELAY_CATEGORIES = [
  "machine_breakdown", "raw_material_shortage", "power_outage",
  "manpower", "packaging_issue", "quality_hold", "other",
];

export default function ManufacturingOpsPanel({ branchId, branchName, allowLogging = true }) {
  const [sub, setSub] = useState("wip");
  const [wip, setWip] = useState([]);
  const [dpr, setDpr] = useState(null);
  const [delays, setDelays] = useState([]);
  const [delayOpen, setDelayOpen] = useState(false);
  const [fgOpen, setFgOpen] = useState(false);
  const [fgBatch, setFgBatch] = useState(null);
  const [fgForm, setFgForm] = useState({ actual_quantity: 0, labour_cost: 0, power_cost: 0, packing_cost: 0, variance_reason: "", notes: "" });
  const today = new Date().toISOString().slice(0, 10);
  const [reportDate, setReportDate] = useState(today);
  const [delayForm, setDelayForm] = useState({
    branch_id: branchId || "",
    date: today,
    category: "machine_breakdown",
    description: "",
    impact_minutes: 0,
    resolution: "",
    production_batch_id: "",
  });

  const load = () => {
    if (!branchId) return;
    api.get("/production/wip", { params: { branch_id: branchId } }).then((r) => setWip(r.data)).catch(() => {});
    api.get("/production/dpr", { params: { branch_id: branchId, date_: reportDate } }).then((r) => setDpr(r.data)).catch(() => {});
    api.get("/production/delays", { params: { branch_id: branchId, from_date: reportDate, to_date: reportDate } }).then((r) => setDelays(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId, reportDate]);
  useEffect(() => { setDelayForm((p) => ({ ...p, branch_id: branchId || "" })); }, [branchId]);

  const saveDelay = async () => {
    if (!delayForm.description || !delayForm.branch_id) return toast.error("Description & branch required");
    try {
      await api.post("/production/delays", {
        ...delayForm,
        impact_minutes: Number(delayForm.impact_minutes) || 0,
        production_batch_id: delayForm.production_batch_id || null,
      });
      toast.success("Delay logged");
      setDelayOpen(false);
      setDelayForm({ ...delayForm, description: "", impact_minutes: 0, resolution: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delDelay = async (id) => {
    if (!window.confirm("Delete this delay entry?")) return;
    try { await api.delete(`/production/delays/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const openFg = (batch) => {
    setFgBatch(batch);
    setFgForm({ actual_quantity: batch.quantity, labour_cost: batch.labour_cost || 0, power_cost: batch.power_cost || 0, packing_cost: batch.packing_cost || 0, variance_reason: "", notes: "" });
    setFgOpen(true);
  };
  const saveFg = async () => {
    if (!fgBatch) return;
    const actual = Number(fgForm.actual_quantity);
    const variance = actual - Number(fgBatch.quantity);
    if (Math.abs(variance) > 0.0001 && !fgForm.variance_reason.trim()) return toast.error("Variance reason required");
    try {
      const r = await api.post(`/production/batches/${fgBatch.id}/receive-fg`, {
        ...fgForm,
        actual_quantity: actual,
        labour_cost: Number(fgForm.labour_cost) || 0,
        power_cost: Number(fgForm.power_cost) || 0,
        packing_cost: Number(fgForm.packing_cost) || 0,
      });
      toast.success(`FG received — batch ${r.data.batch_no} closed${variance ? ` (variance ${variance > 0 ? "+" : ""}${variance})` : ""}`);
      setFgOpen(false); setFgBatch(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const factory = dpr?.factories?.[0];
  const t = factory?.totals;

  return (
    <div className="space-y-4" data-testid="mfg-ops-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="uc-label">Operations</div>
          <h2 className="font-[Outfit] text-2xl font-semibold text-[#2C1810] flex items-center gap-2">
            <Factory className="w-6 h-6 text-[#C05621]" /> Manufacturing Operations {branchName ? <span className="text-base text-[#8B7355] font-normal">· {branchName}</span> : null}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-[#8B7355]">Report date</Label>
          <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="w-40" data-testid="mfg-ops-date" />
        </div>
      </div>

      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="flex flex-wrap h-auto bg-transparent p-0 gap-0">
          <ColorTab value="wip" tone="amber" icon={PlayCircle} testid="mfg-ops-tab-wip">WIP ({wip.length})</ColorTab>
          <ColorTab value="dpr" tone="green" icon={ClipboardList} testid="mfg-ops-tab-dpr">Daily Production Report</ColorTab>
          <ColorTab value="delays" tone="red" icon={AlertOctagon} testid="mfg-ops-tab-delays">Delays ({delays.length})</ColorTab>
        </TabsList>

        {/* WIP */}
        <TabsContent value="wip" className="pt-4">
          <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Batch #</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Warehouse</th>
                  <th className="text-right px-3 py-2">Age</th>
                  <th className="text-left px-3 py-2">QC</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="mfg-ops-wip-rows">
                {wip.map((b) => (
                  <tr key={b.id} className="border-t border-[#EAE5D9]">
                    <td className="px-3 py-2 font-mono text-xs">{b.batch_no}</td>
                    <td className="px-3 py-2">{b.product_name}</td>
                    <td className="px-3 py-2 text-right">{b.quantity}</td>
                    <td className="px-3 py-2 text-xs">{b.warehouse_branch_name || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs">{b.age_hours != null ? `${b.age_hours}h` : "—"}</td>
                    <td className="px-3 py-2"><Badge className={`border-0 ${b.qc_status === "released" ? "bg-emerald-100 text-emerald-800" : b.qc_status === "on_hold" ? "bg-amber-100 text-amber-800" : b.qc_status === "rejected" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>{b.qc_status || "pending"}</Badge></td>
                    <td className="px-3 py-2"><Badge className="bg-amber-100 text-amber-800 border-0 capitalize">{b.status}</Badge></td>
                    <td className="px-3 py-2 text-right">
                      {allowLogging && !b.fg_received_at && (
                        <Button size="sm" onClick={() => openFg(b)} data-testid={`mfg-ops-fg-${b.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Receive FG
                        </Button>
                      )}
                      {b.fg_received_at && <Badge className="bg-emerald-100 text-emerald-800 border-0">received</Badge>}
                    </td>
                  </tr>
                ))}
                {wip.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-[#8B7355]">No batches currently in production 🎉</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* DPR */}
        <TabsContent value="dpr" className="pt-4 space-y-4">
          {!t ? <div className="text-[#8B7355] text-sm py-6 text-center">No data for {reportDate}.</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="mfg-ops-dpr-kpis">
                <Kpi label="Batches" value={t.batches_produced} tone="blue" />
                <Kpi label="FG Units" value={t.fg_units_total} tone="green" icon={PackageCheck} />
                <Kpi label="Raw Lines" value={t.raw_material_lines} tone="slate" />
                <Kpi label="Labour (h)" value={t.labour_hours} tone="orange" />
                <Kpi label="OT (h)" value={t.ot_hours} tone="purple" />
                <Kpi label="Delays" value={t.delays_count} tone={t.delays_count > 0 ? "red" : "slate"} />
                <Kpi label="Delay (min)" value={t.delay_minutes} tone={t.delay_minutes > 0 ? "red" : "slate"} />
                <Kpi label="WIP Open" value={t.wip_open} tone={t.wip_open > 0 ? "amber" : "slate"} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
                  <h3 className="font-[Outfit] text-lg font-semibold mb-2 flex items-center gap-2"><PackageCheck className="w-4 h-4 text-emerald-700" /> FG Produced Today</h3>
                  <table className="w-full text-sm" data-testid="mfg-ops-dpr-fg">
                    <tbody>
                      {factory.fg_produced.map((r, i) => (
                        <tr key={i} className="border-b border-[#EAE5D9] last:border-0"><td className="py-1.5">{r.product}</td><td className="py-1.5 text-right font-semibold text-emerald-700">{r.quantity} units</td></tr>
                      ))}
                      {factory.fg_produced.length === 0 && <tr><td className="py-6 text-center text-[#8B7355]">No FG produced today.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
                  <h3 className="font-[Outfit] text-lg font-semibold mb-2">Raw Materials Consumed</h3>
                  <table className="w-full text-sm" data-testid="mfg-ops-dpr-raw">
                    <tbody>
                      {factory.raw_materials_used.slice(0, 12).map((r, i) => (
                        <tr key={i} className="border-b border-[#EAE5D9] last:border-0"><td className="py-1.5">{r.ingredient}</td><td className="py-1.5 text-right">{r.quantity}</td></tr>
                      ))}
                      {factory.raw_materials_used.length === 0 && <tr><td className="py-6 text-center text-[#8B7355]">No raw material consumed.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="text-[10px] text-[#8B7355] text-right">
                FactoryUnitID: <span className="font-mono">{factory.factory_id}</span>
              </div>
            </>
          )}
        </TabsContent>

        {/* Delays */}
        <TabsContent value="delays" className="pt-4 space-y-3">
          {allowLogging && (
            <div className="flex justify-end">
              <Dialog open={delayOpen} onOpenChange={setDelayOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="mfg-ops-delay-add" className="bg-red-600 hover:bg-red-700 text-white"><Plus className="w-4 h-4 mr-1" /> Log Delay / Issue</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader><DialogTitle>Log Production Delay / Issue</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Date</Label><Input type="date" value={delayForm.date} onChange={(e) => setDelayForm({ ...delayForm, date: e.target.value })} /></div>
                      <div><Label>Category</Label>
                        <Select value={delayForm.category} onValueChange={(v) => setDelayForm({ ...delayForm, category: v })}>
                          <SelectTrigger data-testid="mfg-ops-delay-cat"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white">{DELAY_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div><Label>Description *</Label><Textarea rows={2} value={delayForm.description} onChange={(e) => setDelayForm({ ...delayForm, description: e.target.value })} placeholder="What happened?" data-testid="mfg-ops-delay-desc" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Impact (minutes)</Label><Input type="number" value={delayForm.impact_minutes} onChange={(e) => setDelayForm({ ...delayForm, impact_minutes: e.target.value })} data-testid="mfg-ops-delay-impact" /></div>
                      <div><Label>Batch # (optional)</Label><Input value={delayForm.production_batch_id} onChange={(e) => setDelayForm({ ...delayForm, production_batch_id: e.target.value })} placeholder="paste batch id" /></div>
                    </div>
                    <div><Label>Resolution</Label><Textarea rows={2} value={delayForm.resolution} onChange={(e) => setDelayForm({ ...delayForm, resolution: e.target.value })} placeholder="How was it resolved?" /></div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setDelayOpen(false)}>Cancel</Button><Button onClick={saveDelay} data-testid="mfg-ops-delay-save" className="bg-red-600 text-white">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          <div className="rounded-lg border border-[#EAE5D9] overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF6EE] text-[#8B7355] text-xs uppercase">
                <tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Impact</th><th className="text-left px-3 py-2">Resolution</th><th className="text-left px-3 py-2">By</th><th></th></tr>
              </thead>
              <tbody data-testid="mfg-ops-delay-rows">
                {delays.map((d) => (
                  <tr key={d.id} className="border-t border-[#EAE5D9]">
                    <td className="px-3 py-2 text-xs">{fmtDate(d.date)}</td>
                    <td className="px-3 py-2"><Badge className="bg-red-100 text-red-800 border-0 capitalize">{d.category.replace(/_/g, " ")}</Badge></td>
                    <td className="px-3 py-2 text-xs max-w-md">{d.description}</td>
                    <td className="px-3 py-2 text-right font-semibold text-red-700">{d.impact_minutes || 0} min</td>
                    <td className="px-3 py-2 text-xs">{d.resolution || <span className="text-[#8B7355] italic">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-[#8B7355]">{d.created_by_name || "—"}</td>
                    <td className="px-3 py-2 text-right">{allowLogging && <button onClick={() => delDelay(d.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}</td>
                  </tr>
                ))}
                {delays.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-[#8B7355]">No delays logged for {reportDate}.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mark FG Received dialog */}
      <Dialog open={fgOpen} onOpenChange={setFgOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Receive FG — {fgBatch?.batch_no}</DialogTitle></DialogHeader>
          {fgBatch && (
            <div className="space-y-3">
              <div className="text-sm text-[#8B7355] bg-[#FAF6EE] rounded p-2 border border-[#EAE5D9]">
                <b>{fgBatch.product_name}</b> · planned <b>{fgBatch.quantity}</b> units · warehouse <b>{fgBatch.warehouse_branch_name || "—"}</b>
              </div>
              <div>
                <Label>Actual FG Received *</Label>
                <Input type="number" step="0.01" value={fgForm.actual_quantity} onChange={(e) => setFgForm({ ...fgForm, actual_quantity: e.target.value })} data-testid="mfg-ops-fg-actual" />
                {Math.abs(Number(fgForm.actual_quantity) - Number(fgBatch.quantity)) > 0.0001 && (
                  <div className="text-xs mt-1 text-amber-700">
                    Variance: <b>{(Number(fgForm.actual_quantity) - Number(fgBatch.quantity)).toFixed(2)}</b> units — stock will auto-adjust in warehouse.
                  </div>
                )}
              </div>
              {Math.abs(Number(fgForm.actual_quantity) - Number(fgBatch.quantity)) > 0.0001 && (
                <div><Label>Variance Reason *</Label><Textarea rows={2} value={fgForm.variance_reason} onChange={(e) => setFgForm({ ...fgForm, variance_reason: e.target.value })} placeholder="e.g. 5 units damaged in transit" data-testid="mfg-ops-fg-reason" /></div>
              )}
              <details className="border border-[#EAE5D9] rounded p-2">
                <summary className="text-xs font-semibold text-[#8B7355] cursor-pointer">Finalize costs (optional)</summary>
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <div><Label className="text-xs">Labour</Label><Input type="number" value={fgForm.labour_cost} onChange={(e) => setFgForm({ ...fgForm, labour_cost: e.target.value })} /></div>
                  <div><Label className="text-xs">Power</Label><Input type="number" value={fgForm.power_cost} onChange={(e) => setFgForm({ ...fgForm, power_cost: e.target.value })} /></div>
                  <div><Label className="text-xs">Packing</Label><Input type="number" value={fgForm.packing_cost} onChange={(e) => setFgForm({ ...fgForm, packing_cost: e.target.value })} /></div>
                </div>
              </details>
              <div><Label>Notes</Label><Textarea rows={2} value={fgForm.notes} onChange={(e) => setFgForm({ ...fgForm, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setFgOpen(false)}>Cancel</Button><Button onClick={saveFg} data-testid="mfg-ops-fg-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">Confirm & Close WIP</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  const map = {
    green: "text-emerald-700 border-emerald-200 bg-emerald-50",
    red: "text-red-700 border-red-200 bg-red-50",
    amber: "text-amber-700 border-amber-200 bg-amber-50",
    blue: "text-blue-700 border-blue-200 bg-blue-50",
    purple: "text-purple-700 border-purple-200 bg-purple-50",
    orange: "text-[#C05621] border-orange-200 bg-orange-50",
    slate: "text-stone-700 border-stone-200 bg-stone-50",
  };
  return (
    <div className={`border rounded-md p-3 ${map[tone] || map.slate}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-70">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-70" />}
      </div>
      <div className="text-xl font-[Outfit] font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
